import { ActorApi } from './actor-api';
import { ActorTree, ActorHierarchyItem, createActorTreeFromHierarchyItem } from '../types/actor-types';
import { ConnectorDashboardRequest, ConnectorDashboardResponse, ConnectorCountResponse } from '../types/api-types';

export class ActorRepository {
  constructor(
    private readonly api: ActorApi
  ) {}

  async getActorTree(operatorId: string): Promise<ActorTree[]> {
    try {
      const items: ActorHierarchyItem[] = await this.api.getActorHierarchy(operatorId);
      return items.map(createActorTreeFromHierarchyItem);
    } catch (error) {
      console.error('Failed to fetch actor hierarchy:', error);
      throw error;
    }
  }

  async getActorTreeWithCount(operatorId: string): Promise<{ actors: ActorTree[], totalCount: number }> {
    try {
      const items: ActorHierarchyItem[] = await this.api.getActorHierarchy(operatorId);
      const actors = items.map(createActorTreeFromHierarchyItem);
      return { actors, totalCount: actors.length };
    } catch (error) {
      console.error('Failed to fetch actor hierarchy with count:', error);
      throw error;
    }
  }

  async getConnectors(
    operatorId: string,
    params?: ConnectorDashboardRequest
  ): Promise<ConnectorDashboardResponse> {
    try {
      return await this.api.getConnectors(operatorId, params);
    } catch (error) {
      console.error('Failed to fetch connectors:', error);
      throw error;
    }
  }

  async getAllMachinesForOperator(): Promise<any[]> {
    const PAGE_SIZE = 500;
    const seen = new Set<number>();
    const all: any[] = [];

    const fetchPage = async (skip: number): Promise<void> => {
      const res = await this.api.getAllMachinesForOperator(skip, PAGE_SIZE);
      const rows: any[] = res.rows ?? res.Rows ?? [];
      for (const m of rows) {
        const id = m.machineId ?? m.MachineId;
        if (id != null && !seen.has(id)) { seen.add(id); all.push(m); }
      }
      const count = res.count ?? res.Count;
      if (count != null && skip + PAGE_SIZE < count) await fetchPage(skip + PAGE_SIZE);
    };

    await fetchPage(0);
    return all;
  }

  async getConnectorCount(
    operatorId: string,
    params?: Omit<ConnectorDashboardRequest, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>
  ): Promise<ConnectorCountResponse> {
    try {
      return await this.api.getConnectorCount(operatorId, params);
    } catch (error) {
      console.error('Failed to fetch connector count:', error);
      throw error;
    }
  }
}
